import $ from "jquery";
import ConfirmModal from "pages/common/components/modals/confirm/confirmmodal";
import { DOMAppend } from "util/react-utils";
import Persistence from "pages/common/lib/grabyo-persistence";
import Util from "pages/common/lib/grabyo-util";
import {
    isString,
    isUndefined,
    isNumber,
    isFinite,
    isFunction,
    isObject,
    isArray,
    noop,
    filter
} from "underscore";

import { getFullscreenContainer } from "util/fullscreen-utils";
import type { Tooltip } from "bootstrap";
import type { AllowedMediaType } from "./canvas-utils";

let sessionExpiryModalTriggered = false;

export type Announcement = {
    title: string;
    message: string;
    messageId: string;
    targetPages?: string[];
    activationDate?: number;
    deactivationDate?: number;
};

const DOMUtils = {
    /**
     * Checks whether video is ended
     * @param timeS - Current video time in seconds
     * @param video - Current video DOM node
     * @param playbackDirection - (-1|1)
     * @param customEndTime - Integer
     */
    isVideoEnded: function (
        timeS: number,
        video: HTMLVideoElement,
        playbackDirection: -1 | 1,
        customEndTime?: number
    ) {
        if (!isNumber(timeS)) {
            // return new TypeError("isVideoEnded() - timeS is not a Number");
            return;
        }
        if (!(video instanceof Node)) {
            // return new TypeError("isVideoEnded() - video is not a DOM node");
            return;
        }
        if (!isNumber(playbackDirection)) {
            // return new TypeError("isVideoEnded() - playbackDirection is not a number");
            return;
        }
        if (playbackDirection !== -1 && playbackDirection !== 1) {
            // return new Error("isVideoEnded() - playbackDirection is not a 1 or -1");
            return;
        }
        if (!isUndefined(customEndTime) && !isFinite(customEndTime)) {
            return;
        }
        const endTime = !isUndefined(customEndTime)
            ? customEndTime
            : video.duration;
        return playbackDirection > 0 ? timeS >= endTime : timeS <= 0;
    },

    /**
     * @typedef jQueryElement
     * [onClickWhileDropdownOpen will listen to all the click event when the
     * dropdown is openned, and will close it if a click is performed out of
     * the dropdown element]
     * @param  $dropdown [element to display]
     * @param  fn     [Function to execute to hide the dropdown]
     * @param  ctx    [context to use when calling the callback function]
     */
    onClickWhileDropdownOpen: function (
        $dropdown: JQuery<Element>,
        fn: () => void,
        ctx?: any
    ) {
        //TODO: the jquery stuff is pretty hard to type and we hopefully will remove it at some point
        const callback = function (e: Event) {
            const $target = $(e.target) as unknown as Element; //TODO: techically this should be $(e.target)[0]
            if ($dropdown.has($target).length === 0 && !$dropdown.is($target)) {
                App.Utils.$window.off(`click`, callback);
                if (isFunction(fn)) {
                    fn.call(ctx ? ctx : this);
                }
            }
        };
        App.Utils.$window.click(callback);
    },

    /**
     * Adds buffer events to a video element, allowing to hook into the following:
     * bufferingStarted
     * bufferingStopped
     * @param {Node} video - Node to attach buffering events to
     * @param {Object} options - Buffering checker options
     */
    addVideoBufferingEvents: function (
        video: HTMLVideoElement,
        options: {
            listenForWaitingEvent?: boolean;
            checkBufferInterval?: number;
            onBufferingStarted?: () => void;
            onBufferingStopped?: () => void;
        }
    ) {
        if (!(video instanceof Node)) {
            console.warn(`addBufferEventsToVideo() only accepts DOM nodes`);
            return;
        }

        let checkBuffer: NodeJS.Timer;
        let lastStartPosition: number;

        // Set up default options
        options = {
            listenForWaitingEvent: false,
            checkBufferInterval: 250,
            onBufferingStarted: noop,
            onBufferingStopped: noop,
            ...options
        };

        const onVideoStopPlaying = () => {
            clearInterval(checkBuffer);
            options.onBufferingStopped();
        };

        // Stop checking buffering
        video.addEventListener(`pause`, onVideoStopPlaying);
        video.addEventListener(`ended`, onVideoStopPlaying);

        video.addEventListener(
            `playing`,
            (evt) =>
                (lastStartPosition = (evt.target as HTMLVideoElement)
                    .currentTime)
        );

        video.addEventListener(`play`, () => {
            checkBuffer = setInterval(() => {
                const pos = video.currentTime;
                const btr = video.buffered;
                let timeToEndOfBuffer = 0;

                for (let i = 0; i < btr.length; i++) {
                    if (pos >= btr.start(i) && pos < btr.end(i)) {
                        timeToEndOfBuffer = btr.end(i) - pos;
                    }
                }

                if (
                    timeToEndOfBuffer <= 0.1 &&
                    video.paused === false &&
                    pos - lastStartPosition > 0.5
                ) {
                    video.dispatchEvent(new CustomEvent(`bufferingStarted`));
                    options.onBufferingStarted();
                }

                if (timeToEndOfBuffer > 0.1) {
                    video.dispatchEvent(new CustomEvent(`bufferingStopped`));
                    options.onBufferingStopped();
                }
            }, options.checkBufferInterval);
        });
    },

    getPercentageHovered: function (
        mousePosition: number,
        $el: JQuery<Element>
    ) {
        const relativePosition = mousePosition - $el.offset().left;
        const wrapperWidth = $el.width();
        return 1 - (wrapperWidth - relativePosition) / wrapperWidth;
    },

    getTimeHovered: function (pageX: number): number {
        const percentage = DOMUtils.getPercentageHovered(pageX, this.$wrapper);
        const sectionDuration = this.model.getSectionDuration();

        return this.model.get(`sectionStart`) + sectionDuration * percentage;
    },

    detectScroll: function (
        $el: JQuery<Element>,
        thresoldInPixels: number,
        cb: () => void,
        ctx?: any
    ) {
        return function () {
            const currentTopYPos = $el.scrollTop();
            const currentBottomYPos = currentTopYPos + $el.height();

            if (currentBottomYPos >= $el[0].scrollHeight - thresoldInPixels) {
                if (isFunction(cb)) {
                    cb.call(ctx ? ctx : this);
                }
            }
        };
    },

    stopLoading: function (video: HTMLVideoElement) {
        video.pause();
        video.src = ``;
        video.load();
        video.parentElement.removeChild(video);
    },

    setStyles(el, styles = {}) {
        if (el instanceof HTMLElement && isObject(styles)) {
            for (const key in styles) {
                el.style[key] = styles[key];
            }
        }
    },

    loadImage: function (file: MediaSource) {
        const $promise = $.Deferred();
        const img = new Image();
        img.crossOrigin = `anonymous`;
        img.onload = () => {
            $promise.resolve(img);
        };
        img.onerror = $promise.reject;

        if (isString(file)) {
            img.src = file;
        } else {
            img.src = URL.createObjectURL(file);
        }

        return $promise;
    },

    setAttributes: function (el: HTMLElement, attrs: Record<string, string>) {
        Object.keys(attrs).forEach((attrName) =>
            el.setAttribute(attrName, attrs[attrName])
        );
    },

    /**
     * Registers a tooltip listener on the provided trigger element,
     * once the tooltip is shown, it can be kept alive on hover.
     *
     * @param $trigger
     * @param options
     */
    registerTooltipAndKeepAliveOnHover: function (
        $trigger: JQuery<Element>,
        options: Partial<Tooltip.Options> = {}
    ) {
        options = Object.assign({}, options, {
            trigger: `manual`
        });

        $trigger
            .tooltip(options)
            .on(`mouseenter`, this.onTooltipMouseEnter)
            .on(`mouseleave`, this.onTooltipMouseLeave);
    },

    onTooltipMouseEnter: function () {
        $(this).tooltip(`show`);
        $(`.tooltip`).on(`mouseleave`, () => $(this).tooltip(`hide`));
    },

    onTooltipMouseLeave: function () {
        const tooltip = this;
        setTimeout(() => {
            if (!$(`.tooltip:hover`).length) {
                $(tooltip).tooltip(`hide`);
            }
        }, 300);
    },

    //@ts-ignore: need a more generic interface
    getMediaWidth: (media: AllowedMediaType) => media.videoWidth || media.width,
    getMediaHeight: (media: AllowedMediaType) =>
        //@ts-ignore: need a more generic interface
        media.videoHeight || media.height,

    /**
     * @summary Displays warning modal overlay.
     * @external ConfirmModal
     * @param title Modal title.
     * @param msg Modal message.
     * @param [ctaLabel=`OK`] Main CTA button label. Has default value of 'OK'.
     * @param [cancelButton=false] Have cancel button enabled or not. Has default value of false.
     * @param [confirmCallback=noop] Confirm callback function. Has default value of noop.
     */
    showWarningModal: function (
        title: string,
        msg: string,
        ctaLabel = `OK`,
        confirmCallback = () => {
            sessionExpiryModalTriggered = false;
        },
        cancelCallback = () => {
            sessionExpiryModalTriggered = false;
        }
    ) {
        if (!sessionExpiryModalTriggered) {
            DOMAppend(
                <ConfirmModal
                    title={title}
                    confirmLabel={ctaLabel}
                    confirmCallback={confirmCallback}
                    cancelCallback={cancelCallback}
                >
                    {msg}
                </ConfirmModal>,
                getFullscreenContainer()
            );
        }

        sessionExpiryModalTriggered = true;
    },

    handleUnauthorisedRequest: function () {
        const logout = () => {
            if (App && App.Models && App.Models.user) {
                App.Models.user.logout(true);
                sessionExpiryModalTriggered = false;
            }
        };

        DOMUtils.showWarningModal(
            `Your session has expired`,
            `Your session has expired. Please login again`,
            `OK`,
            logout,
            logout
        );
    },

    preventDragAndDrop: () => {
        const preventDragAndDrop = function (e: Event) {
            e = e || event;
            e.preventDefault();
        };

        window.addEventListener(`dragover`, preventDragAndDrop, false);
        window.addEventListener(`drop`, preventDragAndDrop, false);
        window.addEventListener(`keydown`, function (e) {
            if (e.keyCode === Util.KEY.SPACEBAR && e.target === document.body) {
                e.preventDefault();
            }
        });
    },

    displayAnnouncements: (announcements: Announcement[], pageName: string) => {
        if (isArray(announcements) && announcements.length > 0) {
            const utcTime = new Date().getTime();
            const announcementsSeen =
                Persistence.PermStore.get(`announcementsSeen`) || [];

            const validMessages = filter(
                announcements,
                function (announcement) {
                    let isValid = true;

                    // If the user already read the message
                    if (
                        announcementsSeen.indexOf(announcement.messageId) !== -1
                    ) {
                        isValid = false;
                    }

                    // If an activation date is set and is in the future
                    if (
                        !isUndefined(announcement.activationDate) &&
                        announcement.activationDate > utcTime
                    ) {
                        isValid = false;
                    }

                    // If an expiration date is set and is in the past
                    if (
                        !isUndefined(announcement.deactivationDate) &&
                        announcement.deactivationDate < utcTime
                    ) {
                        isValid = false;
                    }

                    // If the message should be displayed on that page
                    if (!isUndefined(announcement.targetPages)) {
                        if (
                            announcement.targetPages.indexOf(pageName) === -1 &&
                            announcement.targetPages.indexOf(`*`) === -1
                        ) {
                            isValid = false;
                        }
                    } else {
                        // If targetPages isn't set the announcement is considered
                        // invalid because we don't know where to display it
                        isValid = false;
                    }

                    return isValid;
                }
            );

            if (validMessages.length > 0) {
                // If we match multiple messages that are valid we only
                // display the first one...
                const messageToDisplay = validMessages[0];

                DOMAppend(
                    <ConfirmModal
                        title={messageToDisplay.title}
                        size="small"
                        confirmCallback={() => {}}
                    >
                        {messageToDisplay.message}
                    </ConfirmModal>,
                    getFullscreenContainer()
                );

                announcementsSeen.push(messageToDisplay.messageId);
                Persistence.PermStore.set(
                    `announcementsSeen`,
                    announcementsSeen
                );
            }
        }
    },

    listenToVisibilityChange: (callback = noop) =>
        document.addEventListener(
            getVisibilityChangeEventName(),
            callback,
            false
        ),

    stopListeningToVisibilityChange: (callback = noop) =>
        document.removeEventListener(getVisibilityChangeEventName(), callback),

    isDocumentHidden: () => {
        if (typeof document.hidden !== `undefined`) {
            return document.hidden;
            //@ts-ignore: feature detection
        } else if (typeof document.msHidden !== `undefined`) {
            //@ts-ignore: feature detection
            return document.msHidden;
            //@ts-ignore: feature detection
        } else if (typeof document.webkitHidden !== `undefined`) {
            //@ts-ignore: feature detection
            return document.webkitHidden;
        }
    }
};

const getVisibilityChangeEventName = () => {
    if (typeof document.hidden !== `undefined`) {
        return `visibilitychange`;
        //@ts-ignore: feature detection
    } else if (typeof document.msHidden !== `undefined`) {
        return `msvisibilitychange`;
        //@ts-ignore: feature detection
    } else if (typeof document.webkitHidden !== `undefined`) {
        return `webkitvisibilitychange`;
    }
};

export default DOMUtils;
